import { CheckCircle, Error } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FC, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import BodyPaper from '../../components/Layout/BodyPaper'
import Header from '../../components/Layout/Header'
import { AlertType, useAlert } from '../../providers/alert'
import { useAuth } from '../../providers/auth'
import { colors } from '../../theme/options'
import { ConsentStatus } from '../../types/Account'

/**
 * `CompleteRequestPageError` enumerates possible error states.
 */
enum CompleteRequestPageError {
  /**
   * `MissingData` means data required to show `CompleteRequestPage` is missing or invalid.
   */
  MissingData = 'missingData',
}

const CompleteRequestPage: FC = () => {
  const { t } = useTranslation('vpc', { keyPrefix: 'completeRequestPage' })
  const { authState } = useAuth()
  const handledErrorsRef = useRef<Set<string>>(new Set())
  const { showAlert } = useAlert()
  const [hasMissingDataError, setHasMissingDataError] = useState(false)

  // display error alert to user if missing data; log warning if request date time
  // is in the future
  useEffect(() => {
    const error = CompleteRequestPageError.MissingData
    const grantedDateTime = authState.accountInfo?.consent.grantedDateTime || ''
    const parentEmail = authState.accountInfo?.consent.parentEmail
    const consentStatus = authState.accountInfo?.consent.status
    if (
      !parentEmail ||
      !consentStatus ||
      !grantedDateTime ||
      isNaN(new Date(grantedDateTime).getTime())
    ) {
      if (handledErrorsRef.current.has(error)) {
        return
      }
      handledErrorsRef.current.add(error)
      setHasMissingDataError(true)
      showAlert({
        severity: AlertType.ERROR,
        msg: `${t('statusMessage.errorLabel')}: ${t(`statusMessage.error.${error}.brief`)}`,
        duration: 5000,
      })
    } else if (handledErrorsRef.current.has(error)) {
      setHasMissingDataError(false)
      handledErrorsRef.current.delete(error)
    }

    if (new Date(grantedDateTime).getTime() > Date.now()) {
      console.warn(`granted date time is in the future: "${grantedDateTime}"`)
    }
    if (consentStatus !== ConsentStatus.APPROVED) {
      console.warn(`consent status is not approved: "${consentStatus}"`)
    }
  }, [authState.accountInfo?.consent, t, showAlert])

  return (
    <>
      <Header
        showPlatformDisplayName
        title={t('title')}
        subtitle={t('subtitle')}
        data-testid="complete-request-title"
      />
      <BodyPaper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          pt: 17.75,
        }}
        showExpiration={true}
        showLogoutButton={true}
        data-testid="complete-request-body"
      >
        {hasMissingDataError ? (
          <Error sx={{ color: useTheme().palette.error.light, width: '4rem', height: '4rem' }} />
        ) : (
          <CheckCircle htmlColor={colors.successGreen} sx={{ width: '4rem', height: '4rem' }} />
        )}
        <Typography
          variant="bodyExtraLarge"
          sx={{ textAlign: 'center' }}
          data-testid="complete-request-status-message"
        >
          {hasMissingDataError ? (
            t(`statusMessage.error.${CompleteRequestPageError.MissingData}.detailed`)
          ) : (
            <Trans
              t={t}
              i18nKey={'statusMessage.approved'}
              values={{
                grantedRequestDateTime: new Date(
                  authState.accountInfo?.consent.grantedDateTime || '',
                ),
                parentEmail: authState.accountInfo?.consent.parentEmail,
              }}
            />
          )}
        </Typography>
      </BodyPaper>
    </>
  )
}

export default CompleteRequestPage
